// The plan's snap set: built once per drawing revision (snapIndex.ts), asked per
// pointer move — the candidate points, each kind's edges (SNAP_EDGES), the
// anchored and edge snaps, the dimension and move snaps, the alignment guides.
// docs/claude/snapping.md.
import {
  type DimAnchor,
  type ProductInstance,
  type Sheet,
  type Vec2,
} from '../../shared/types';
import { selectedOf } from '../selection';
import { ceilingSnapMarks } from '../ceiling';
import {
  drawingRev,
  objHidden,
  state,
  vp,
} from './core';
import { drawingEditor } from './drawingEditor';
import {
  distance,
  distToSegment,
  drawsHeaterBlock,
  instanceToWorld,
  moveAlignOffset,
  perpFoot,
  pickSnapCandidate,
  planSnapEdges,
  planSnapPoints,
  projectToSegment,
  segSegIntersect,
  snapDraftGuides,
} from '../geometry';
import { type ItemKind } from '../layers';
import {
  isPolyRoom,
  polyBenchBands,
  polyEdges,
  polyInner,
  polyInnerBenchBands,
} from '../polyRoom';
import {
  instanceCorners,
  pdfBoxCorners,
} from '../render';
import { sectionEdges } from '../section';
import { axisSnapOffset } from '../snap';
import {
  edgeIndex,
  pointIndex,
  type SnapIndex,
} from '../snapIndex';
import { textBoxCorners } from '../textBox';
import {
  bodyOf,
  isTilted,
  planOutline,
} from '../tilt';
import { partHeight } from '../heights';
import { wallSolidRuns } from '../wallFeatures';
import {
  bgImages,
  centroidSnapForPoint,
  draftLockStep,
  editorShapeSnapVerts,
  gsnapPt,
  importedHidden,
  importedLayer,
  instanceProbeCorners,
  objectCentroid,
  planMoveCarry,
  rcpWorldPieces,
  sectionSources,
  SNAP_POINTS,
  SNAP_PRIORITY,
  SNAP_PX,
  snapExcludes,
} from '../main';
import { ceilingMarksNow } from './ceilingUi';
import { drawDraft } from './distanceEntry';
import {
  heaterOutlines,
  type SnapCandidate,
} from '../anchors';

/** This module's state that main.ts reads and writes too. */
export const snapSetUi = {
  // The other dimensions' endpoints (+ the first pick) the live dimension may align
  // to, kept from the last snap so the purple guides can be drawn to them.
  dimAlignVerts: [] as Vec2[],
  // Purple alignment guides (world segments) for the current image/PDF move drag —
  // from an aligned corner of the moved underlay to the object vertex it lined up on.
  moveAlignGuides: [] as Array<{ from: Vec2; to: Vec2 }>,
};

// ---- The snap set: built once per drawing revision, asked per pointer move ----
/** What a move, a draw tool or a dimension snaps to, for one set of objects
 *  left out (snapIndex.ts indexes it): the points, and the runs once asked. */
interface SnapSetNow {
  points: readonly SnapCandidate[];
  near: SnapIndex<SnapCandidate>;
  edges?: SnapIndex<SnapEdge>;
}
const snapSets = new Map<string, SnapSetNow>();
const idsKey = (v: ReadonlySet<string> | string | null | undefined): string => (v == null ? '' : typeof v === 'string' ? v : [...v].sort().join(','));
/** Everything a snap set reads: the drawing (its revision) and what outside it
 *  shows or hides a candidate — the left-out ids, the hidden layers, the sheet,
 *  Show ceilings and the selection (a selected room shows its ceiling's marks),
 *  the disciplines shown, the drawing editor. */
function snapSetKey(except: ReadonlySet<string> | string | null, hidden: ReadonlySet<string> | undefined, sheet: Sheet | undefined): string {
  return [drawingRev(), idsKey(except), idsKey(hidden), sheet ? `${sheet.id}:${sheet.showCeilings === true}` : '', state.showCeilings, selectedOf(state, 'instance').join(','),
    JSON.stringify(state.disciplineFilters), drawingEditor.drawingEditId ?? ''].join('|');
}
/** The snap set for what is left out, rebuilt only when the drawing's revision
 *  (or another input, `snapSetKey`) moves: a draw tool's hover builds it once. */
function snapSet(except: ReadonlySet<string> | string | null, hidden?: ReadonlySet<string>, sheet?: Sheet): SnapSetNow {
  const key = snapSetKey(except, hidden, sheet);
  let set = snapSets.get(key);
  if (set) { snapSets.delete(key); snapSets.set(key, set); return set; }
  const points = Object.freeze(buildSnapCandidates(except, hidden, sheet));
  set = { points, near: pointIndex(points) };
  snapSets.set(key, set);
  while (snapSets.size > 6) snapSets.delete(snapSets.keys().next().value!);
  return set;
}
/** Every snap point, for what is left out (the snap set's, never rebuilt here). */
export function snapCandidates(except: ReadonlySet<string> | string | null, hidden?: ReadonlySet<string>, sheet?: Sheet): readonly SnapCandidate[] {
  return snapSet(except, hidden, sheet).points;
}
/** The runs the edge snaps derive from, indexed, for what is left out. */
function snapEdges(except: ReadonlySet<string> | string | null): SnapIndex<SnapEdge> {
  const set = snapSet(except);
  return (set.edges ??= edgeIndex(Object.freeze(collectSnapEdges(except))));
}

function buildSnapCandidates(except: ReadonlySet<string> | string | null, hidden?: ReadonlySet<string>, sheet?: Sheet): SnapCandidate[] {
  const isExcluded = snapExcludes(except, hidden);
  const impHidden = importedHidden() || !!hidden?.has(importedLayer().id);
  const pts: SnapCandidate[] = [];
  for (const kind of Object.keys(SNAP_POINTS) as ItemKind[]) {
    SNAP_POINTS[kind]?.(pts, isExcluded, hidden, sheet);
    if (kind !== 'instance') continue;
    // The imported plan and the underlays (the Imported layer, no ItemKind) come
    // right after the parts and rooms.
    const bg = impHidden ? null : state.drawing.background;
    if (bg) {
      // Shifted by the plan's move offset — reading the raw entities put every one
      // of these snaps back where the plan was IMPORTED, so once it had been moved
      // the marker no longer sat on the geometry under the cursor.
      for (const c of planSnapPoints(bg.entities, state.drawing.backgroundOffset)) {
        pts.push(c.center ? { p: c.p, kind: 'center' } : { p: c.p });
      }
    }
    // Raster underlays (imported PDFs / images): their four (rotated) corners, so a
    // dimension can be taken off a scanned plan's own edges.
    for (const bi of impHidden ? [] : bgImages()) {
      if (bi.id && isExcluded(bi.id)) continue;
      for (const v of pdfBoxCorners(bi)) pts.push({ p: v });
    }
  }
  // In the drawing editor, snap to the edited instance's own shape vertices too.
  if (drawingEditor.drawingEditId) for (const v of editorShapeSnapVerts()) pts.push({ p: v });
  return pts;
}

/** Edges the object-snap pass derives midpoint / perpendicular / nearest /
 *  intersection points from: user polygons, walls & lines, instance footprints,
 *  dimensions and the imported plan's line/polyline segments.
 *  The third element marks a run that is a CURVE'S TESSELLATION rather than a
 *  drawn edge — see `edgeSnapCandidates`, which must not invent points from it. */
type SnapEdge = [Vec2, Vec2, boolean?];
/** What each drawn kind offers `collectSnapEdges`: the runs the midpoint,
 *  nearest, perpendicular and intersection snaps are derived from — the twin of
 *  SNAP_POINTS (a kind listed in one and not the other snaps to its corners but
 *  not the run between them). Every ItemKind has an entry; the keys run in the
 *  order the list is built. */
type SnapEdgesOf = (out: SnapEdge[], ring: (pts: Vec2[], closed: boolean, curve?: boolean) => void, isExcluded: (id: string) => boolean) => void;
const SNAP_EDGES: Record<ItemKind, SnapEdgesOf | null> = {
  polygon: (out, ring, isExcluded) => {
    for (const poly of state.drawing.polygons || []) {
      if (isExcluded(poly.id)) continue;
      ring(poly.points, true, poly.ellipse); // a circle/ellipse ring is a TESSELLATION
      // A hole's loops are runs like any other; `snapCandidates` offers their
      // corners, so without these the midpoint of a cut-out's side is unsnappable.
      for (const h of poly.holes || []) ring(h, true);
    }
  },
  wall: (out, ring, isExcluded) => {
    for (const wall of state.drawing.walls || []) {
      if (isExcluded(wall.id)) continue;
      // The centre-line: its nodes are snap candidates (they are the draggable ones),
      // so the run between them has to be too.
      ring(wall.points, false);
      // …and BOTH FACES of the band, the twin of the band corners `snapCandidates`
      // already offers. Without them a wall contributed corners but no runs, so the
      // perpendicular foot, the nearest point and the face midpoint all landed on
      // the centre-line — buried inside the wall, half its thickness from whichever
      // side you were pointing at, and out of reach entirely on a thick one.
      // `wallBand` returns the left face plus the reversed right face, so one closed
      // ring is both sides and the two end caps. A 'line' wall is drawn AS its
      // centre-line and has no band, exactly as in `snapCandidates`.
      // Read off its solid pieces: a door's jambs are runs too, and a closed wall is
      // its two faces with no seam across its joint.
      if (wall.style !== 'line') for (const loop of wallSolidRuns(wall).flat()) ring(loop, true);
    }
  },
  instance: (out, ring, isExcluded) => {
    for (const inst of state.drawing.instances) {
      if (isExcluded(inst.id)) continue;
      if (inst.autoBenches && isPolyRoom(inst)) {
        // A walled room's real edges — outer walls, the inside faces of the walls,
        // and every bench band — so dimensions snap along the inside of the room
        // and to benches (nearest / midpoint / perpendicular), not just corners.
        const toW = (p: Vec2): Vec2 => instanceToWorld(inst, p);
        for (const e of polyEdges(inst)) { out.push([toW(e.a), toW(e.b)]); out.push([toW(e.ia), toW(e.ib)]); }
        ring(polyInner(inst).map(toW), true);
        for (const band of polyBenchBands(inst)) if (band.poly.length >= 2) ring(band.poly.map(toW), true);
        for (const ib of polyInnerBenchBands(inst)) if (ib.poly.length >= 2) ring(ib.poly.map(toW), true);
        const cm = ceilingMarksNow(inst);
        if (cm) for (const [a, b, curve] of ceilingSnapMarks(cm).runs) out.push([toW(a), toW(b), curve]);
        continue;
      }
      if (inst.nodes && inst.nodes.length) ring(inst.nodes, false);
      else if (drawsHeaterBlock(inst)) for (const r of heaterOutlines(inst)) ring(r.map((p) => instanceToWorld(inst, p)), true);
      // A tilted part: the outline the plan draws it by, from above.
      else if (isTilted(inst)) ring(planOutline(bodyOf(inst, 0, { w: inst.width, d: inst.depth, h: partHeight(inst) })), true);
      else ring(instanceCorners(inst), true);
    }
  },
  dim: (out, ring, isExcluded) => {
    for (const d of state.drawing.dimensions) if (!isExcluded(d.id)) out.push([d.a, d.b]);
  },
  // The kinds `snapCandidates` already contributes vertices for contribute their
  // edges here too, or the cursor snaps to their corners but not to the midpoint
  // or the nearest point of the run between them — the two lists have to cover
  // the same objects for "snap to every object" to mean one thing.
  flooring: (out, ring, isExcluded) => {
    for (const f of state.drawing.floorings || []) { if (!isExcluded(f.id)) ring(f.points, true); }
  },
  cloud: (out, ring, isExcluded) => {
    for (const c of state.drawing.revisionClouds || []) { if (!isExcluded(c.id)) ring(c.points, true); }
  },
  leader: (out, ring, isExcluded) => {
    for (const l of state.drawing.leaders || []) { if (!isExcluded(l.id)) ring(l.points, false); }
  },
  text: (out, ring, isExcluded) => {
    for (const t of state.drawing.texts || []) {
      if (isExcluded(t.id)) continue;
      ring(textBoxCorners(t), true);
    }
  },
  rcp: (out, ring, isExcluded) => {
    // A reflected ceiling plan's lines, the twin of its points.
    for (const v of state.drawing.ceilingPlans || []) {
      if (isExcluded(v.id)) continue;
      for (const pc of rcpWorldPieces(v)) if (pc.kind === 'line') ring(pc.pts, pc.closed);
    }
  },
  section: (out, ring, isExcluded) => {
    // A generated section's runs, the twin of the vertices `snapCandidates` takes:
    // without these its corners snapped but the wall line between them did not.
    for (const sec of state.drawing.sections || []) {
      if (isExcluded(sec.id)) continue;
      const sv = sectionSources();
      for (const e of sectionEdges(sec, sv.instances, sv.walls, sv.polygons, sv.pipes)) out.push(e);
    }
  },
  // An arc offers its ends and centre (SNAP_POINTS); its curve gives no runs yet.
  arc: null,
  // The schedule tables: see SNAP_POINTS.
  table: null,
  // A connection runs between objects that snap by their own runs.
  conn: null,
};

function collectSnapEdges(except: ReadonlySet<string> | string | null): SnapEdge[] {
  const isExcluded = snapExcludes(except);
  const out: SnapEdge[] = [];
  const ring = (pts: Vec2[], closed: boolean, curve = false): void => {
    const n = pts.length, segs = closed ? n : n - 1;
    for (let i = 0; i < segs; i++) out.push([pts[i], pts[(i + 1) % n], curve]);
  };
  for (const kind of Object.keys(SNAP_EDGES) as ItemKind[]) {
    if (kind === 'section') {
      // A raster underlay (the Imported layer, no ItemKind), before the sections.
      // A raster underlay's (rotated) box — its corners are snap candidates, so a
      // dimension must be able to take the midpoint of a scanned plan's edge too.
      for (const bi of importedHidden() ? [] : bgImages()) {
        if (bi.id && isExcluded(bi.id)) continue;
        ring(pdfBoxCorners(bi), true);
      }
    }
    SNAP_EDGES[kind]?.(out, ring, isExcluded);
  }
  const bg = importedHidden() ? null : state.drawing.background;
  // Shifted by the plan's move offset, exactly as `snapCandidates` shifts its
  // vertices through `planSnapPoints` — the plan is DRAWN through that offset.
  // Read raw, every derived candidate (midpoint / nearest / perpendicular /
  // intersection) sat where the plan was imported: once it had been moved,
  // pointing at a wall on the plan snapped to nothing, while empty space back at
  // the old position still did.
  if (bg) for (const e of planSnapEdges(bg.entities, state.drawing.backgroundOffset)) out.push(e);
  return out;
}

/** The "from" point a perpendicular snap measures against: the last drawn vertex
 *  or the dimension's first pick. Null when no drawing is in progress. */
function perpAnchor(): Vec2 | null {
  return drawDraft()?.prev ?? state.pendingA ?? null;
}

/** Object-snap candidates derived from nearby edges: midpoints, nearest-on-edge,
 *  perpendicular feet (from the active anchor) and pairwise intersections. Only
 *  edges within `tol`·2.5 of the cursor are considered, capped for the O(n²)
 *  intersection step. Respects the per-kind OSNAP toggles. */
function edgeSnapCandidates(world: Vec2, anchor: Vec2 | null, except: ReadonlySet<string> | string | null, tol: number): SnapCandidate[] {
  const os = state.osnap;
  if (!os.midpoint && !os.nearest && !os.perpendicular && !os.intersection) return [];
  const near = snapEdges(except).near(world, tol * 2.5 + 1e-6)
    .map((e) => ({ e, d: distToSegment(world, e[0], e[1]) }))
    .filter((x) => x.d < tol * 2.5)
    .sort((a, b) => a.d - b.d)
    .slice(0, 12)
    .map((x) => x.e);
  const out: SnapCandidate[] = [];
  // A CURVE'S TESSELLATION is not a set of drawn edges, and the points its chords
  // imply are artefacts of how finely it happens to be sampled. Two of them are
  // actively harmful now that precedence beats distance, because both outrank the
  // centre and the quadrants — the points a curve really has:
  //  - a chord's MIDPOINT is inside the curve, not on it;
  //  - two adjacent chords "intersect" at a ring vertex, so the whole 64-point
  //    ring came straight back as `intersection` candidates, which is exactly the
  //    set of fake corners `snapCandidates` stopped offering.
  // `nearest` and the perpendicular foot stay: both are real points ON the run,
  // and at 64 samples a chord is within 0.1% of the arc.
  for (const [a, b, curve] of near) {
    if (os.midpoint && !curve) out.push({ p: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, kind: 'midpoint' });
    if (os.nearest) out.push({ p: projectToSegment(world, a, b), kind: 'nearest' });
    if (os.perpendicular && anchor) {
      const foot = perpFoot(anchor, a, b);
      // Keep it only when the foot lands on the segment itself (not its extension).
      if (distance(foot, projectToSegment(foot, a, b)) < 1e-6) out.push({ p: foot, kind: 'perpendicular' });
    }
  }
  if (os.intersection) {
    for (let i = 0; i < near.length; i++) for (let j = i + 1; j < near.length; j++) {
      // Where a curve CROSSES something else is a real intersection; where its own
      // chords meet is not. (Two different curves crossing is left out with them —
      // that point is a tessellation artefact either way.)
      if (near[i][2] && near[j][2]) continue;
      const hit = segSegIntersect(near[i][0], near[i][1], near[j][0], near[j][1]);
      if (hit) out.push({ p: hit.pt, kind: 'intersection' });
    }
  }
  return out;
}

/** Snap a world point to the nearest candidate, returning the point and its
 *  anchor: within `tol` mm (the plan's aperture at its zoom, unless a tool aimed
 *  elsewhere — the 3D view — says how many mm its aperture is there). */
export function snapPointAnchored(world: Vec2, exceptId: ReadonlySet<string> | string | null, extra?: Vec2[], tol = SNAP_PX / vp.scale): { point: Vec2; anchor?: DimAnchor; snapped?: boolean } {
  if (!state.snapEnabled) {
    state.snapIndicator = null;
    state.snapIndicatorKind = null;
    return { point: world };
  }
  // The aperture rule itself is pure and unit-tested (`pickSnapCandidate`): within
  // a near-tie the OSNAP precedence decides, not raw distance, so the corner the
  // cursor is aimed at wins over a midpoint or perpendicular foot a millimetre
  // nearer. That matters more since an acquired snap beats the guides — the pick
  // is final, so whatever it lands on is what the click takes.
  const best = pickSnapCandidate<SnapCandidate>(
    world,
    [...snapSet(exceptId).near.near(world, tol), ...(extra ?? []).map((p): SnapCandidate => ({ p })), ...edgeSnapCandidates(world, perpAnchor(), exceptId, tol)],
    tol,
    SNAP_PRIORITY,
    state.osnap,
  );
  state.snapIndicator = best ? best.p : null;
  state.snapIndicatorKind = best ? (best.kind ?? 'endpoint') : null;
  // `snapped` says a real candidate was ACQUIRED — not just that an anchor came
  // with it (most candidates carry none). Callers need it to let an object snap
  // override the alignment guides, so the marker never promises a vertex the pick
  // does not actually land on.
  return best ? { point: best.p, anchor: best.anchor, snapped: true } : { point: world };
}

/** Snap a single world point to the nearest candidate within threshold (`tol`
 *  mm: the plan's aperture by default). */
export function snapPoint(world: Vec2, exceptId: string | null, tol?: number): Vec2 {
  return snapPointAnchored(world, exceptId, undefined, tol).point;
}

/** Where a dragged RESIZE handle lands: on another object's vertex (the OSNAP,
 *  with the resized object left out — its own corners move with the handle and
 *  would catch it) when one is in the aperture, else on the grid. Alt = grid only. */
export function resizeHandleSnap(world: Vec2, self: string | null, alt: boolean): { p: Vec2; snapped: boolean } {
  if (alt) { state.snapIndicator = null; state.snapIndicatorKind = null; return { p: gsnapPt(world), snapped: false }; }
  const s = snapPointAnchored(world, self);
  return s.snapped ? { p: s.point, snapped: true } : { p: gsnapPt(world), snapped: false };
}

/** Snap a moved underlay (image/PDF or DXF plan) so a corner aligns to another
 *  object's vertex on X and/or Y, recording the purple guides. `raw` is the
 *  grid-snapped centre before alignment; `cornersAt(c)` gives the body's corners
 *  for a candidate centre. Returns the aligned centre. Shift or Snap-off skips it. */
export function alignMovedUnderlay(raw: Vec2, cornersAt: (c: Vec2) => Vec2[], shift: boolean): Vec2 {
  snapSetUi.moveAlignGuides = [];
  if (shift || !state.snapEnabled) return raw;
  const targets = snapCandidates(null).map((c) => c.p);
  const res = moveAlignOffset(cornersAt(raw), targets, SNAP_PX / vp.scale);
  if (!res) return raw;
  snapSetUi.moveAlignGuides = res.guides;
  return { x: raw.x + res.off.x, y: raw.y + res.off.y };
}

/** Every existing dimension's two endpoints — the alignment targets offered while
 *  drawing a new dimension. `exceptId` drops one (unused while drawing, there for
 *  symmetry with the section resolver). */
function dimensionVertices(exceptId?: string | null): Vec2[] {
  const out: Vec2[] = [];
  for (const d of state.drawing.dimensions ?? []) {
    if ((exceptId && d.id === exceptId) || objHidden(d.id)) continue; // a hidden one guides nothing
    out.push({ ...d.a }, { ...d.b });
  }
  return out;
}

/** Snap for the dimension tool: object-snap (room walls / benches / corners), then
 *  the shared draft-guide layer — an ortho / polar lock relative to the first pick
 *  (`from`) when ORTHO or POLAR is on, and alignment to the horizontal / vertical
 *  projections of the OTHER dimensions' endpoints and (for the second pick) the
 *  first one, drawn as purple guides. When a guide pulls the point off a snapped
 *  feature, its anchor is dropped (it no longer sits on that feature). */
export function snapDimPoint(world: Vec2, from: Vec2 | null): { point: Vec2; anchor?: DimAnchor } {
  const s = snapPointAnchored(world, null);
  const verts = state.snapEnabled
    ? [...(from ? [from] : []), ...dimensionVertices()]
    : [];
  snapSetUi.dimAlignVerts = verts;
  // An acquired OSNAP WINS over every guide — the AutoCAD rule, and the only one
  // that makes the marker honest. The guides used to run on top of the snap and
  // drag the point off the vertex whenever it sat within a few px of the first
  // pick's row/column, which is the common case when dimensioning between two
  // objects: the figure came out as the axis distance instead of the true
  // corner-to-corner one, and the marker was cleared, so nothing said why.
  if (s.snapped) return s;
  const guided = snapDraftGuides(s.point, verts, SNAP_PX / vp.scale, {
    allow45: true,
    lastSeg: from ? [from, from] : undefined,
    lockStep: draftLockStep(),
  });
  if (distance(guided, s.point) > 1e-6) {
    state.snapIndicator = null;
    state.snapIndicatorKind = null;
    return { point: guided };
  }
  return s;
}

/**
 * Shared move-snap core: given the moving selection's probe points and centroid,
 * return the offset that best aligns it to nearby geometry. Vertex/corner snapping
 * on each axis, overridden per-axis by centroid-to-centroid orthogonal alignment
 * (which shows a red guide). `exclude` are the ids whose own points must not be
 * snap targets; `skipGroupId` also excludes a moving group from centroid matching.
 */
export function snapMoveOffset(probes: Vec2[], centroid: Vec2, exclude: ReadonlySet<string> | string | null, skip?: ReadonlySet<string>): Vec2 {
  if (!state.snapEnabled) {
    state.snapIndicator = null;
    state.centroidGuides = [];
    return { x: 0, y: 0 };
  }
  const tol = SNAP_PX / vp.scale;
  const result = axisSnapOffset(probes, snapCandidates(exclude).map((c) => c.p), tol);
  state.snapIndicator = result.at;
  const c = centroidSnapForPoint(centroid, exclude, tol, skip);
  return { x: c.lockX ? c.off.x : result.offset.x, y: c.lockY ? c.off.y : result.offset.y };
}

/** What a dragged instance takes along — itself and, for a room, what the drag
 *  started with standing in it — none of which may be its own snap target. */
export function movingWith(inst: ProductInstance): Set<string> {
  return new Set([inst.id, ...(inst.autoBenches ? planMoveCarry().map((m) => m.id) : [])]);
}
/** What a dragged instance's centre does not align to: what it takes along, and
 *  the rest of its group. */
export function centroidSkip(inst: ProductInstance): Set<string> {
  const out = movingWith(inst);
  if (inst.groupId) for (const m of state.drawing.instances) if (m.groupId === inst.groupId) out.add(m.id);
  return out;
}

/**
 * Snap a moving instance: try to align any of its corners/centre to a nearby
 * candidate point, returning the offset to apply to the instance.
 */
export function snapInstanceOffset(inst: ProductInstance): Vec2 {
  // Probes/candidates include centres, so centres align to each other (and edges
  // to edges) on each axis independently. Node objects probe their real vertices.
  const probes = inst.nodes && inst.nodes.length
    ? inst.nodes.map((n) => ({ x: n.x, y: n.y }))
    : [...instanceProbeCorners(inst), { x: inst.x, y: inst.y }];
  return snapMoveOffset(probes, objectCentroid(inst), movingWith(inst), centroidSkip(inst));
}
